import { ApiError } from "@/shared/api/client";

/** Field-level validation message returned by the API, if any. */
export const fieldError = (error: unknown, field: string) =>
  error instanceof ApiError ? error.fields[field] : undefined;

/** Vietnamese text for the AI module's error codes (shared/ai shows them too). */
export const AI_CODE_MESSAGES: Record<string, string> = {
  ai_disabled: "Tính năng AI đang tắt (công tắc tổng hoặc tính năng này chưa bật).",
  premium_required: "Tính năng này chỉ dành cho tài khoản Premium.",
  ai_quota_exceeded: "Đã hết lượt dùng AI hôm nay. Thử lại vào ngày mai hoặc tăng hạn mức.",
  ai_unavailable: "Nhà cung cấp AI đang lỗi, quá thời gian hoặc chưa được cấu hình. Thử lại sau.",
  ai_invalid_output: "AI trả về kết quả không đúng định dạng. Thử lại hoặc chỉnh prompt.",
  ai_input_flagged: "Nội dung gửi lên bị bộ lọc an toàn chặn.",
  ai_encryption_key_missing: "Máy chủ chưa cấu hình AI_ENCRYPTION_KEY nên chưa lưu được API key.",
};

/**
 * Vietnamese text for the codes of the server's document reader (PDF/DOCX
 * uploads: course import, documents as AI sources).
 */
export const DOCUMENT_ERROR_MESSAGES: Record<string, string> = {
  unsupported_file: "Tệp không phải PDF hoặc DOCX hợp lệ.",
  pdf_encrypted: "PDF đang đặt mật khẩu hoặc bị khoá: mở khoá (in lại ra PDF) rồi tải lên lại.",
  pdf_damaged: "Tệp PDF bị hỏng, không đọc được. Hãy xuất lại PDF từ tài liệu gốc.",
  docx_macros: "Tệp Word có macro không được nhận. Hãy lưu lại thành .docx thường (không macro).",
  file_required: "Chưa chọn tệp tài liệu.",
  file_too_large: "Tệp quá lớn (tối đa 20 MB).",
  scanned_pdf: "PDF này là bản scan (ảnh), không có chữ để đọc. Hãy dùng PDF có chữ chọn được hoặc tệp DOCX.",
  document_too_long: "Tài liệu quá dài (tối đa 300 trang, khoảng 200 000 ký tự). Hãy tách thành nhiều tệp nhỏ hơn.",
  document_empty: "Không tìm thấy nội dung chữ trong tài liệu.",
  document_unreadable: "Không đọc được tài liệu này (tệp có thể bị hỏng hoặc quá phức tạp). Hãy xuất lại PDF/DOCX từ tài liệu gốc rồi tải lên lại.",
  extraction_busy: "Máy chủ đang bận đọc tài liệu khác, thử lại sau ít phút.",
  extraction_canceled: "Việc đọc tài liệu bị dừng giữa chừng (kết nối bị ngắt hoặc máy chủ đang khởi động lại). Tải lên lại.",
};

/** Vietnamese text for API codes editors meet often (the API's own messages are English). */
const CODE_MESSAGES: Record<string, string> = {
  published_content: "Nội dung đang xuất bản (hoặc chứa bài học đã xuất bản). Lưu trữ trước khi xoá.",
  // Codes used before published_content, kept while an older API may answer.
  course_published: "Khóa học đang xuất bản. Lưu trữ trước khi xoá.",
  reading_published: "Bài đọc đang xuất bản. Lưu trữ trước khi xoá.",
  publish_blocked: "Khóa học còn lỗi cần sửa trước khi xuất bản.",
  export_too_large: "Quá nhiều lượt làm bài để xuất một lần. Thu hẹp bộ lọc (khoá học, bài, khoảng ngày) rồi thử lại.",
  level_exists: "Ngôn ngữ này đã có cấp độ cùng mã.",
  essay_empty: "Học viên không viết gì: không có bài để chấm.",
  essay_not_found: "Bài tập essay không còn (đã xoá hoặc không phải essay).",
  essay_answer_not_found: "Không tìm thấy bài viết này trong lượt làm (lượt làm có thể đã bị xoá).",
  level_in_use: "Cấp độ đang được dùng. Chuyển khóa học, bài đọc và từ vựng sang cấp độ khác trước khi xoá.",
  // Classrooms (admin): the backend's problem codes for the "Lớp học" screens.
  classroom_not_found: "Không tìm thấy lớp (có thể đã bị xoá cùng tài khoản giáo viên).",
  post_not_found: "Thông báo không còn (giáo viên có thể đã xoá).",
  classroom_read_only: "Lớp đang lưu trữ hoặc bị khoá nên không thay đổi được.",
  classroom_not_active: "Chỉ khoá được lớp đang hoạt động.",
  not_teacher: "Học viên chưa có quyền giáo viên.",
  theory_missing: "Chủ đề của bài chưa có lý thuyết đã lưu. Soạn và lưu lý thuyết trước.",
  unit_not_found: "Không tìm thấy chủ đề (có thể đã bị xoá).",
  // Push automations ("Thông báo › Tự động").
  automation_limit: "Đã có 50 tự động đang chạy (mức tối đa). Tạm dừng hoặc lưu trữ một tự động khác rồi kích hoạt lại.",
  automation_trigger_locked: "Tự động đã từng kích hoạt nên không đổi được trình kích hoạt. Hãy tạo tự động mới với trình kích hoạt khác.",
  learner_not_found: "Không có tài khoản học viên với email này. Đăng nhập app bằng email đó trước.",
  no_devices: "Tài khoản này chưa có thiết bị nào bật thông báo. Mở app, đăng nhập và cho phép thông báo.",
  automation_archived: "Tự động đã lưu trữ: không sửa và không kích hoạt lại được. Hãy tạo tự động mới.",
  // Friends ("Bạn bè" on the learner page, "Báo cáo người dùng").
  report_resolved: "Báo cáo này đã được xử lý (có thể bởi admin khác). Danh sách vừa được tải lại.",
  report_not_found: "Không tìm thấy báo cáo (tài khoản liên quan có thể đã bị xoá).",
  friend_not_found: "Hai học viên không còn là bạn bè (có thể đã tự gỡ kết bạn hoặc chặn nhau).",
  // Weekly leagues & quest templates.
  league_cohort_not_found: "Không tìm thấy nhóm giải đấu này.",
  quest_template_not_found: "Không tìm thấy mẫu nhiệm vụ (có thể vừa bị xoá).",
  // Hán Việt readings.
  hanviet_syllables_mismatch: "Âm Hán Việt phải có đúng một âm tiết cho mỗi chữ Hán của từ.",
  hanviet_char_not_found: "Chữ này không có bản sửa của admin (có thể vừa được khôi phục).",
  hanviet_word_not_found: "Không tìm thấy từ trong bảng âm Hán Việt (có thể vừa bị xoá).",
  // Course import (tạo khóa học từ tài liệu).
  ...DOCUMENT_ERROR_MESSAGES,
  language_not_found: "Không tìm thấy ngôn ngữ đã chọn (có thể vừa bị xoá). Tải lại trang rồi chọn lại.",
  level_not_found: "Không tìm thấy cấp độ đã chọn (có thể vừa bị xoá). Tải lại trang rồi chọn lại.",
  invalid_multipart: "Dữ liệu tải lên không hợp lệ. Tải lại trang rồi thử lại.",
  course_import_not_found: "Không tìm thấy bản nhập (có thể đã bị xoá).",
  course_not_found: "Không tìm thấy khóa học (có thể đã bị xoá).",
  body_too_large: "Dữ liệu gửi lên quá lớn (bản nháp hoặc tệp vượt giới hạn của máy chủ).",
  malformed_json: "Dữ liệu gửi lên sai định dạng. Tải lại trang rồi thử lại.",
  body_timeout: "Mạng chậm nên dữ liệu chưa gửi kịp tới máy chủ, chưa có gì được lưu. Đừng tải lại trang (sẽ mất phần đang sửa): bấm lưu lại khi mạng ổn định hơn.",
  empty_body: "Yêu cầu thiếu dữ liệu. Tải lại trang rồi thử lại.",
  import_not_ready: "Bản nháp chưa sẵn sàng (đang xử lý, đã huỷ hoặc đã tạo khóa học).",
  import_running: "Đang xử lý tài liệu: huỷ trước rồi mới xoá được.",
  generation_failed: "AI không soạn được khóa học từ tài liệu này. Thử lại, hoặc thêm hướng dẫn rõ hơn.",
  // Language centers (docs/b2b-centers-contract.md).
  org_not_found: "Không tìm thấy trung tâm (hoặc bạn không quản lý trung tâm này).",
  user_not_found: "Không có tài khoản học viên nào dùng e-mail này. Giáo viên cần đăng nhập ứng dụng một lần trước.",
  teacher_in_other_org: "Giáo viên này đang thuộc một trung tâm khác.",
  org_seats_full: "Trung tâm đã dùng hết số chỗ (seat) của giấy phép.",
  org_licence_inactive: "Giấy phép của trung tâm chưa bắt đầu, đã hết hạn hoặc trung tâm đang bị khoá.",
  invite_redeemed: "Mã mời này đã được dùng, không thu hồi được.",
  branch_name_taken: "Đã có chi nhánh trùng tên.",
  not_center_admin: "Tài khoản này không có vai trò Quản lý trung tâm. Đổi vai trò ở trang Nhân sự trước.",
  parent_consent_required: "Cần xác nhận đã có sự đồng ý trước khi lưu e-mail phụ huynh.",
  invalid_csv: "Tệp CSV cần dòng tiêu đề: ho_ten, ma_hoc_vien, email_phu_huynh, ma_lop.",
  import_too_large: "Tối đa 500 dòng mỗi lần nhập.",
  // Billing (Gói Premium).
  billing_plan_exists: "Đã có gói với mã này. Chọn mã khác.",
  billing_product_taken: "Mã sản phẩm App Store/Google Play này đang thuộc một gói khác.",
  billing_plan_not_found: "Không tìm thấy gói (có thể vừa bị đổi). Tải lại trang.",
  ...AI_CODE_MESSAGES,
};

/** Human-readable message for any thrown value (API errors carry the request id for support). */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return `${CODE_MESSAGES[error.code] ?? error.message}${error.requestId ? ` (mã: ${error.requestId})` : ""}`;
  if (error instanceof Error) return error.message;
  return "Đã có lỗi xảy ra";
}
